import { useMutation } from '@apollo/client';
import {
  OverrideChangeRequestByIdDocument,
  UpdateApproverResponsesDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type {
  OverrideChangeRequestInput,
  OverrideChangeRequestLevelInput,
} from './useOverrideChangeRequestTRPC';
import { useOverrideChangeRequestTRPC } from './useOverrideChangeRequestTRPC';

/**
 * Flag-gated CR override (RSP-1528 §11). Consumers import THIS — not the
 * `*TRPC` variant. `trpc` on → tRPC `changeRequest.override`; otherwise the
 * legacy Apollo `OverrideChangeRequestById` mutation. Both accept the same
 * `{ changeRequestId, comment, approved }` shape. Errors reject (no toast
 * here) — the calling modal surfaces them, matching the `*TRPC`-hook rule
 * that data hooks don't own UI side-effects.
 */
export const useOverrideChangeRequest = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [overrideGraphQL, overrideState] = useMutation(
    OverrideChangeRequestByIdDocument,
    {
      update: (cache) => {
        evictField(cache, 'change_request_by_pk');
      },
    }
  );

  const [overrideLevelGraphQL, overrideLevelState] = useMutation(
    UpdateApproverResponsesDocument,
    {
      update: (cache) => {
        evictField(cache, 'change_request_by_pk');
      },
    }
  );

  const trpcHook = useOverrideChangeRequestTRPC();

  const overrideChangeRequest = async (
    input: OverrideChangeRequestInput
  ): Promise<void> => {
    if (trpcEnabled) {
      await trpcHook.overrideChangeRequest(input);

      return;
    }

    await overrideGraphQL({
      variables: {
        Id: input.changeRequestId,
        Rationale: input.comment ?? '-',
        Approved: input.approved,
      },
    });
  };

  const overrideChangeRequestLevel = async (
    input: OverrideChangeRequestLevelInput
  ): Promise<void> => {
    if (trpcEnabled) {
      await trpcHook.overrideChangeRequestLevel(input);

      return;
    }

    await overrideLevelGraphQL({
      variables: {
        input: {
          ChangeRequestId: input.changeRequestId,
          Response: input.approved,
          // The Apollo UpdateApproverResponsesInput.Comment is non-null;
          // the modal always passes a constructed string, `?? ''` satisfies
          // the type for the nullable wrapper input.
          Comment: input.comment ?? '',
          OverrideLevel: true,
          LevelId: input.levelId,
        },
      },
    });
  };

  return {
    overrideChangeRequest,
    overrideChangeRequestLevel,
    loading: trpcEnabled
      ? trpcHook.loading
      : overrideState.loading || overrideLevelState.loading,
  };
};
